import React from 'react';
import { CircularProgress } from '@mui/material';
import { closeSnackbar, enqueueSnackbar, SnackbarKey } from 'notistack';
import { patchMoveFolders } from '../api/foldersApi';
import { FOLDER_TYPE, PATHS } from '../enums';
import { store } from '../store/store';
import { updateTriggerFolderFetch } from '../store/folders/foldersSlice';
import { refetchFeeds } from '../store/videoFeedsList/videoFeedsListSlice';

const UNDO_DURATION = 10000;
let moveVersion = 0;
let snackbarKey: SnackbarKey | undefined;
let expiryTimer: ReturnType<typeof setTimeout> | undefined;

const clearMoveUndo = () => {
  if (expiryTimer !== undefined) clearTimeout(expiryTimer);
  if (snackbarKey !== undefined) closeSnackbar(snackbarKey);
  expiryTimer = undefined;
  snackbarKey = undefined;
};

// One pending action for the whole app; a newer move invalidates older callbacks.
export const beginMoveUndo = () => {
  const version = ++moveVersion;
  clearMoveUndo();
  const user = store.getState().user.currentUser;
  const sameUser = () => {
    const current = store.getState().user.currentUser;
    return !!user && current?.id === user.id && current?.organization?.id === user.organization?.id;
  };

  return (message: string, undo: () => Promise<unknown>) => {
    if (version !== moveVersion || !sameUser()) return;
    const expiresAt = Date.now() + UNDO_DURATION;
    const dismiss = () => {
      if (version !== moveVersion) return;
      ++moveVersion;
      clearMoveUndo();
    };
    const onUndo = async () => {
      if (version !== moveVersion) return;
      if (Date.now() >= expiresAt || !sameUser()) {
        dismiss();
        return;
      }
      dismiss();
      const loadingKey = enqueueSnackbar('Undoing move…', {
        variant: 'info',
        persist: true,
        action: () => <CircularProgress size={18} color="inherit" aria-label="Undoing move" />,
      });
      try {
        const result = await undo();
        enqueueSnackbar(typeof result === 'string' ? result : 'Move undone', { variant: 'success' });
      } catch (error: any) {
        enqueueSnackbar(error?.response?.data?.message || 'Could not undo the move', { variant: 'error' });
      } finally {
        closeSnackbar(loadingKey);
        store.dispatch(updateTriggerFolderFetch());
        store.dispatch(refetchFeeds());
      }
    };

    snackbarKey = enqueueSnackbar(message, {
      variant: 'success',
      persist: true,
      action: () => (
        <span style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={onUndo}>Undo</button>
          <button type="button" onClick={dismiss}>Dismiss</button>
        </span>
      ),
      onClose: dismiss,
    });
    expiryTimer = setTimeout(dismiss, UNDO_DURATION);
  };
};

export const rememberFolderLocations = (folders: Pick<FOLDER_TYPE, 'id' | 'parent_folder_id'>[]) => {
  const groups = new Map<string | null, string[]>();
  folders.forEach((folder) => {
    const parentId = folder.parent_folder_id == null ? null : String(folder.parent_folder_id);
    groups.set(parentId, [...(groups.get(parentId) || []), String(folder.id)]);
  });
  return async () => {
    for (const [parentId, ids] of groups) {
      await patchMoveFolders(
        ids,
        parentId,
        encodeURI(parentId
          ? `${PATHS.VIDEOFEEDS_LIST.path}?folderId=${parentId}&parentPage=${PATHS.MY_FOLDERS.label}`
          : PATHS.MY_FOLDERS.path),
        encodeURI(parentId
          ? `${PATHS.SHARED_VIDEOFEEDS_LIST.path}?folderId=${parentId}&parentPage=${PATHS.SHARED_WITH_ME.label}`
          : PATHS.SHARED_WITH_ME.path),
      );
    }
  };
};
